import React from 'react';
import { PostType } from '../types/postTypes';
import { getFileUrl } from '../helpers/axios';

// Union type for all media elements
type MediaElement = HTMLImageElement | HTMLVideoElement | HTMLAudioElement | HTMLIFrameElement;

interface MediaRendererProps {
  fileUrl: string;
  postType?: PostType;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  width?: number | string;
  height?: number | string;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  onError?: (e: React.SyntheticEvent<MediaElement, Event>) => void;
  placeholder?: string;
}

/**
 * Helper function to detect external video platform URLs
 */
const isExternalVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  const externalPatterns = [
    'youtube.com',
    'youtu.be',
    'vimeo.com',
    'dailymotion.com',
    'dai.ly'
  ];
  return externalPatterns.some(pattern => lowerUrl.includes(pattern));
};

/**
 * Get YouTube embed URL from various URL formats
 */
const getYouTubeEmbedUrl = (url: string): string | null => {
  if (!url) return null;
  const lowerUrl = url.toLowerCase();
  
  if (lowerUrl.includes('youtube.com/watch')) {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  }
  if (lowerUrl.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  }
  if (lowerUrl.includes('youtube.com/embed/')) {
    return url; // Already an embed URL
  }
  return null;
};

/**
 * Get Vimeo embed URL
 */
const getVimeoEmbedUrl = (url: string): string | null => {
  if (!url) return null;
  const lowerUrl = url.toLowerCase();
  
  if (lowerUrl.includes('vimeo.com/')) {
    const videoId = url.split('vimeo.com/')[1]?.split('/')[0]?.split('?')[0];
    return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
  }
  if (lowerUrl.includes('player.vimeo.com/video/')) {
    return url; // Already an embed URL
  }
  return null;
};

/**
 * Get Dailymotion embed URL
 */
const getDailymotionEmbedUrl = (url: string): string | null => {
  if (!url) return null;
  const lowerUrl = url.toLowerCase();
  
  if (lowerUrl.includes('dailymotion.com/video/')) {
    const videoId = url.split('/video/')[1]?.split('_')[0]?.split('?')[0];
    return videoId ? `https://www.dailymotion.com/embed/video/${videoId}` : null;
  }
  if (lowerUrl.includes('dai.ly/')) {
    const videoId = url.split('dai.ly/')[1]?.split('?')[0];
    return videoId ? `https://www.dailymotion.com/embed/video/${videoId}` : null;
  }
  return null;
};

/**
 * MediaRenderer Component
 * Renders appropriate media element based on post type and file content
 * Enhanced to support external video platforms (YouTube, Vimeo, Dailymotion)
 */
const MediaRenderer: React.FC<MediaRendererProps> = ({
  fileUrl,
  postType = PostType.NORMAL_POST,
  alt = "Content",
  className = "",
  style = {},
  width,
  height,
  controls = true,
  autoPlay = false,
  muted = true,
  onError,
  placeholder
}) => {
  // Check if it's an external video platform URL
  const isExternalVideo = isExternalVideoUrl(fileUrl);
  
  // Determine if content is video based on post type or file extension
  const isVideo = !isExternalVideo && (
    postType === PostType.VIDEO_POST || 
    postType === PostType.SHORT_VIDEO_POST ||
    fileUrl?.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/)
  );

  // Determine if content is audio/podcast
  const isAudio = postType === PostType.PODCAST_POST ||
                  fileUrl?.toLowerCase().match(/\.(mp3|wav|aac|m4a|ogg|flac|wma)$/);

  // Default placeholder for broken media
  const defaultPlaceholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk1lZGlhIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4=';

  const handleError = (e: React.SyntheticEvent<MediaElement, Event>) => {
    if (isVideo) {
      // For videos, try to fallback to a video placeholder or thumbnail
      console.error('Failed to load video:', fileUrl);
    } else if (isAudio) {
      // For audio, log the error
      console.error('Failed to load audio:', fileUrl);
    } else {
      // For images, set a placeholder
      const target = e.currentTarget;
      if (target && 'src' in target) {
        (target as HTMLImageElement).src = placeholder || defaultPlaceholder;
      }
    }
    
    if (onError) {
      onError(e);
    }
  };

  const mediaStyle = {
    ...style,
    width: width || style.width,
    height: height || style.height,
  };

  if (!fileUrl) {
    return (
      <div 
        className={`d-flex align-items-center justify-content-center bg-light ${className}`}
        style={{
          ...mediaStyle,
          minWidth: width || 200,
          minHeight: height || 150,
          border: '1px dashed #ccc',
          borderRadius: '4px'
        }}
      >
        <span className="text-muted">No media available</span>
      </div>
    );
  }

  // Handle external video platforms (YouTube, Vimeo, Dailymotion)
  if (isExternalVideo) {
    const lowerUrl = fileUrl.toLowerCase();
    let embedUrl: string | null = null;
    let platformName = 'External Video';
    
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
      embedUrl = getYouTubeEmbedUrl(fileUrl);
      platformName = 'YouTube';
    } else if (lowerUrl.includes('vimeo.com')) {
      embedUrl = getVimeoEmbedUrl(fileUrl);
      platformName = 'Vimeo';
    } else if (lowerUrl.includes('dailymotion.com') || lowerUrl.includes('dai.ly')) {
      embedUrl = getDailymotionEmbedUrl(fileUrl);
      platformName = 'Dailymotion';
    }
    
    if (embedUrl) {
      return (
        <div 
          className={`ratio ratio-16x9 ${className}`} 
          style={{
            ...mediaStyle,
            minWidth: width || 320,
            minHeight: height || 180,
            borderRadius: '4px',
            overflow: 'hidden'
          }}
        >
          <iframe
            src={embedUrl}
            title={`${platformName} Video`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onError={handleError as any}
          />
        </div>
      );
    }
    
    // Fallback for unsupported external video URLs
    return (
      <div 
        className={`d-flex flex-column align-items-center justify-content-center bg-light ${className}`}
        style={{
          ...mediaStyle,
          minWidth: width || 200,
          minHeight: height || 150,
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          padding: '1rem'
        }}
      >
        <span className="text-muted mb-2">External Video</span>
        <a 
          href={fileUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn btn-sm btn-outline-primary"
        >
          Open in new tab
        </a>
      </div>
    );
  }

  const fullUrl = getFileUrl(fileUrl);

  if (isVideo) {
    return (
      <video
        className={className}
        style={mediaStyle}
        controls={controls}
        autoPlay={autoPlay}
        muted={muted}
        onError={handleError}
        preload="metadata"
      >
        <source src={fullUrl} type="video/mp4" />
        <source src={fullUrl} type="video/webm" />
        <source src={fullUrl} type="video/ogg" />
        Your browser does not support the video tag.
      </video>
    );
  }

  if (isAudio) {
    return (
      <audio
        className={className}
        style={mediaStyle}
        controls={controls}
        autoPlay={autoPlay}
        onError={handleError}
        preload="metadata"
      >
        <source src={fullUrl} type="audio/mpeg" />
        <source src={fullUrl} type="audio/wav" />
        <source src={fullUrl} type="audio/ogg" />
        <source src={fullUrl} type="audio/aac" />
        Your browser does not support the audio element.
      </audio>
    );
  }

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      style={mediaStyle}
      onError={handleError}
    />
  );
};

export default MediaRenderer;
